import type { ChartGranularity } from '@skystone/shared';
import {
  type ManagementDatePreset,
  getManagementPresetRange,
  getGranularity,
} from '@/lib/management-date-presets';

// `'custom'` is a sentinel preset for an arbitrary user-picked `from`/`to`
// range (PRODOPSAI-648). It never flows through `getManagementPresetRange`
// (its range lives in the URL `?from`/`?to` params, resolved by the hook), so
// `getPillRange` / `getPillGranularity` short-circuit it.
export type MetricViewPreset = ManagementDatePreset | 'custom';

export const METRIC_VIEW_DEFAULT_PRESET: MetricViewPreset = '4w';

/** Sentinel pill id for the custom date-range mode. */
export const METRIC_VIEW_CUSTOM_PILL_ID = 'custom';

const CURRENT_YEAR = new Date().getUTCFullYear();
const YEAR_PILL_OFFSETS = [-1, 0] as const;

export interface IMetricViewTimePill {
  id: string;
  label: string;
  preset: MetricViewPreset;
  year?: number;
}

export const METRIC_VIEW_TIME_PILLS: ReadonlyArray<IMetricViewTimePill> = [
  { id: '2w', label: '2w', preset: '2w' },
  { id: '4w', label: '4w', preset: '4w' },
  { id: '8w', label: '8w', preset: '8w' },
  { id: 'Q1', label: 'Q1', preset: 'Q1' },
  { id: 'Q2', label: 'Q2', preset: 'Q2' },
  { id: 'Q3', label: 'Q3', preset: 'Q3' },
  { id: 'Q4', label: 'Q4', preset: 'Q4' },
  { id: 'YTD', label: 'YTD', preset: 'YTD' },
  ...YEAR_PILL_OFFSETS.map((offset) => {
    const year = CURRENT_YEAR + offset;
    return { id: `year-${year}`, label: String(year), preset: 'full-year' as const, year };
  }),
];

const DEFAULT_PILL: IMetricViewTimePill =
  METRIC_VIEW_TIME_PILLS.find((p) => p.id === METRIC_VIEW_DEFAULT_PRESET) ??
  { id: METRIC_VIEW_DEFAULT_PRESET, label: METRIC_VIEW_DEFAULT_PRESET, preset: METRIC_VIEW_DEFAULT_PRESET };

/**
 * The synthetic pill used while a custom `from`/`to` range is active. It is
 * intentionally NOT a member of `METRIC_VIEW_TIME_PILLS` — the preset pill row
 * renders it as a separate "Custom" button. `filters.pill` is set to this when
 * the hook detects a valid custom range in the URL.
 */
export const METRIC_VIEW_CUSTOM_PILL: IMetricViewTimePill = {
  id: METRIC_VIEW_CUSTOM_PILL_ID,
  label: 'Custom',
  preset: 'custom',
};

export function resolvePreset(pillId: string): IMetricViewTimePill {
  return METRIC_VIEW_TIME_PILLS.find((p) => p.id === pillId) ?? DEFAULT_PILL;
}

// AC1.1.3: legacy values from saved/shared links resolve to YTD instead of erroring.
// Stale `year-NNNN` ids — for any year that no longer has a pill (the year row
// rolls forward each January) — are caught dynamically so we don't have to
// remember to update a hard-coded list.
const KNOWN_PILL_IDS = new Set(METRIC_VIEW_TIME_PILLS.map((p) => p.id));

export function parseMetricViewPillParam(value: string | null): IMetricViewTimePill {
  if (!value) return resolvePreset(METRIC_VIEW_DEFAULT_PRESET);
  if (value === 'full-year') return resolvePreset('YTD');
  if (/^year-\d{4}$/.test(value) && !KNOWN_PILL_IDS.has(value)) {
    return resolvePreset('YTD');
  }
  return resolvePreset(value);
}

export function getPillRange(pill: IMetricViewTimePill): { from: string; to: string } {
  if (pill.preset === 'custom') {
    // Custom ranges are resolved from the URL ?from/?to params by the hook,
    // never from the pill. Return today as a harmless fallback; consumers in
    // custom mode never read this.
    const today = new Date().toISOString().slice(0, 10);
    return { from: today, to: today };
  }
  const year = pill.year ?? CURRENT_YEAR;
  return getManagementPresetRange(pill.preset, year);
}

export function getPillGranularity(pill: IMetricViewTimePill): ChartGranularity {
  // Custom granularity depends on the span, which the pill does not carry —
  // the hook computes it via getCustomGranularity. 'weekly' is a safe default.
  if (pill.preset === 'custom') return 'weekly';
  return getGranularity(pill.preset);
}

/**
 * Granularity for an arbitrary custom range (PRODOPSAI-648). Mirrors the spirit
 * of the preset granularities: short ranges get fine buckets, long ones coarse.
 *   span ≤ 45 days  → daily   (≈ the 2w/4w presets)
 *   span ≤ 213 days → weekly  (≈ the 8w/quarter/YTD presets)
 *   else            → monthly (≈ the full-year preset)
 */
export function getCustomGranularity(from: string, to: string): ChartGranularity {
  const spanDays = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
  if (spanDays <= 45) return 'daily';
  if (spanDays <= 213) return 'weekly';
  return 'monthly';
}

/** Type-guard: a non-empty `YYYY-MM-DD` string that is a real calendar date. */
export function isValidIsoDate(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return false;
  // Date.parse normalizes impossible dates (e.g. 2026-02-31 → Mar 3), so a
  // round-trip back to YYYY-MM-DD must equal the input to reject non-real days.
  return new Date(ms).toISOString().slice(0, 10) === value;
}

/**
 * Human-readable sublabel for the "Latest" copy on each DORA card. Per
 * PRODOPSAI-324 epic D14: rolling pills use "Last N weeks", quarter pills use
 * "Q1 2026" (current year), YTD uses "YTD", Full Year uses "Full year 2026",
 * year pills use just the year string.
 */
export function formatPillSubLabel(pill: IMetricViewTimePill): string {
  if (pill.year !== undefined) {
    return String(pill.year);
  }
  switch (pill.preset) {
    case '2w': return 'Last 2 weeks';
    case '4w': return 'Last 4 weeks';
    case '8w': return 'Last 8 weeks';
    case 'Q1':
    case 'Q2':
    case 'Q3':
    case 'Q4':
      return `${pill.preset} ${CURRENT_YEAR}`;
    case 'YTD': return 'YTD';
    case 'full-year': return `Full year ${CURRENT_YEAR}`;
    default: return pill.label;
  }
}

export { getManagementPresetRange };
export {
  isRollingPreset,
  parseManagementPresetParam,
  parseYearParam,
} from '@/lib/management-date-presets';
